import { OnInit, Component, EventEmitter, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { TrucksService, TankerData, Truck } from 'src/app/transportation/trucks/trucks.service';
import { forkJoin } from 'rxjs';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { Router } from '@angular/router';
import { DocumentService } from 'src/app/documents/documents.service';
import { saveAs } from 'file-saver';
import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';
import _ from 'lodash';
import { PgTitleComponent, PgToolbarComponent } from '../../../shared/components/title-bar/title-bar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { ModuleRegistry, AllCommunityModule, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { TruckToolbarComponent } from '../trucks-toolbar/truck-toolbar/truck-toolbar.component';
import moment from 'moment';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-tanker-list',
  templateUrl: './tanker-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule],
})
export class TankerListComponent implements OnInit, PgTitleComponent, PgToolbarComponent {
  constructor(
    private _truckService: TrucksService,
    private _router: Router,
    private _organisationService: OrganisationService,
    private _documentsService: DocumentService,
    private _pgMessagesService: PgMessagesService,
    private _renderer: Renderer2,
    private _userLocalStorage: UserLocalStoreService
  ) { }

  title = new EventEmitter<string>();
  toolbarComponentType = TruckToolbarComponent;
  toolbarComponent!: TruckToolbarComponent;

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  columnDefs = [
    {
      headerName: 'Edit', cellRenderer: (params:ICellRendererParams) => {
        const button = AgGridService.editButton(this._renderer, this._router, this.editRecord, params.data);
        return button;
      },
    },

    { headerName: 'Number', field: 'number'},
    {
      field: 'owner',
      headerName: 'Owner',
      valueFormatter: (params:ICellRendererParams) => this.formatOrganisation(params.value),
      filterValueGetter: (params:ICellRendererParams) => this.formatOrganisation(params.data.owner),
    },

    { headerName: 'Capacity', field: 'capacity' },

    {
      headerName: 'License Expire Date', field: 'licenseExpireDate', cellRenderer: (params:ICellRendererParams) => {
        const date = moment(params.data.licenseExpireDate);
        const isExpired = date.isBefore(this.today);
        const color = isExpired ? 'red' : 'inherit';
        return `<span style="color:${color}">${date.format('YYYY/MM/DD')}</span>`;
      }
    },

    { headerName: 'License Number',
      field: 'licenseNumber',
      filter: 'agTextColumnFilter',
      filterValueGetter: (params:ICellRendererParams) =>  params.data.licenseNumber.toString(),
    },

    {
      headerName: 'Download', field: 'fileId', cellRenderer: (params:ICellRendererParams) => AgGridService.downloadButton(
        this._renderer,
        () => this.downloadFile(params.data.fileId, params.data.number),
      )
    },

    { headerName: 'Status', cellRenderer: (params: ICellRendererParams) => this.checkUsed(params.data) }
  ];

  tankers!: TankerData[];
  tankersCash: {
    [key: string]: TankerData;
  } = {};
  today!: number;

  _organisationsCache: {
    [key: string]: Organisation;
  } = {};

  ngOnInit() {
    this.title.emit('Tankers');
    this.today = new Date().getTime();
    this.gridOptions = AgGridService.getOptions();
    forkJoin([
      this._truckService.getAllTankers(),
      this._organisationService.get(),
      this._truckService.getForLpgDist()
    ]).subscribe(results => {
      this.tankers = results[0];
      results[1].forEach(o => {
        this._organisationsCache[o.name] = o;
      });
      this.tankersCash = this.tankersMap(results[2]);
    });
    this.toolbarComponent.url = 'trucks/tanker';
  }

  formatOrganisation(organisationId: string) {
    const foundOrganisation = this._organisationsCache[organisationId];
    if (foundOrganisation) {
      return foundOrganisation.nameTranslations['fa'];
    } else {
      return '';
    }
  }

  editRecord(_router: Router, tanker: TankerData) {
    _router.navigate(['trucks/tanker', tanker.number, tanker.owner]);
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe(b => {
      saveAs(b.body, `${prefix}.pdf`);
    }, () => {
      this._pgMessagesService.publishMessage({
        message: 'Could not download this file.',
        level: MessageLevel.Error,
        topic: 'Voyage document'
      });
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'tankersGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'tankersGrid');
  }

  tankersMap(trucks: Truck[]) {
    const usedTankers = trucks.filter(t => t.tanker)
      .map(t1 => t1.tanker?.associatedObject);
    const grouped = _.groupBy(usedTankers, (item) => item.number + '$' + item.owner);
    return _(grouped).map((items, key) => {
      const split = key.split('$');
      return {
        number: split[0],
        owner: split[1],
      };
    }).value().reduce((hashMap: Record<string, any>, data) => {
      hashMap[data.number + data.owner] = data;
      return hashMap;
    }, {});
  }

  checkUsed(tanker: TankerData) {
    return (this.tankersCash[tanker.number + tanker.owner]) ? 'used' : 'notUsed';
  }

}